import { NetworkStatus } from '@apollo/client';
import { FORM_ERROR } from 'final-form';
import { cond, constant, isEmpty, isFunction, isNull, last, negate, noop, stubTrue } from 'lodash';
import {
	compose,
	filter,
	first,
	groupBy,
	has,
	map,
	mapValues,
	path,
	pipe,
	prop,
	reduce,
	set,
	unionBy,
} from 'lodash/fp';

import { omitDeep } from '@skippay/core/utils/objectUtils';

/**
 * Extracts array of GraphQL errors from ApolloError
 * @param {import(`@apollo/client`).ApolloError} apolloError
 * @return {import(`graphql`).GraphQLError[]}
 */
const getGraphQLErrors = prop('graphQLErrors');
/**
 * Extracts AxiosErrorResponse from GraphQL error
 * @param {import(`graphql`).GraphQLError} graphQLError
 * @return {import(`axios`).AxiosResponse}
 */
const extractAxiosErrors = path(['extensions', 'response']);
/**
 * Extracts single AxiosErrorResponse from ApolloError. Works only when query or mutation contains single field
 * selection, when operation contains more than one field, use `toErrorsByField` to get errors grouped by fieldName.
 * @param {import(`@apollo/client`).ApolloError} apolloError
 * @return {import(`axios`).AxiosResponse}
 */
export const firstAxiosError = compose(extractAxiosErrors, first, getGraphQLErrors);
/**
 * Given ApolloError returns dictionary(fieldName => AxiosErrorResponse)
 * @param {import(`@apollo/client`).ApolloError} apolloError
 * @return {Record<string, import(`axios`).AxiosResponse>}
 */
export const toErrorsByField = compose(
	mapValues(compose((error) => extractAxiosErrors(error) || error, first)),
	groupBy('fieldName'),
	getGraphQLErrors
);
/**
 * Given AxiosErrorResponse containing a HTTP 422 validation error response,
 * returns a dictionary(attribute => errorMessage) with non-attribute errors
 * grouped as an array under property FORM_ERROR.
 * @param {import(`axios`).AxiosResponse} axiosError
 * @return {Record<string, string> & Record<FORM_ERROR, string[]>|undefined}
 */
export const toValidationErrors = (axiosError) =>
	axiosError?.data?.errors?.reduce((acc, { attribute, message }) => {
		if (attribute) {
			return set(attribute, message, acc);
		}
		acc[FORM_ERROR] = (acc[FORM_ERROR] || []).concat(message);
		return acc;
	}, {});
/**
 * Given ApolloError, returns final-form validation errors or undefined when ApolloError does not contain a validation
 * error. When the error contains non-field error (whole form error) it is stored under special final-form's FORM_ERROR
 * key as an array of all such errors.
 * !!! Assumes mutation contains single field selection (takes errors for the first field only).
 * @param {import(`@apollo/client`).ApolloError} apolloError
 *@return {Record<string, string> & Record<FORM_ERROR, string[]>|undefined}
 */
export const getValidationError = compose(toValidationErrors, firstAxiosError);

/**
 * Finds `originalError` in `ApolloError` or returns `ApolloError`
 */
export const getOriginalError = (apolloError) => firstAxiosError(apolloError) || apolloError;

/**
 * Rethrows `originalError` from `ApolloError`, useful for extracting
 * `originalError` in rejected promise chain
 */
export const rethrowOriginalError = (error) => {
	throw getOriginalError(error);
};

/**
 * Gets error code from `originalError`
 */
export const getErrorCode = prop('status');

const isValidationError = has('attribute');

/**
 * Gets error messages from `originalError`
 */
export const getErrorMessages = pipe(
	path(['data', 'errors']),
	filter(negate(isValidationError)),
	cond([
		[isEmpty, constant(undefined)],
		[stubTrue, map('message')],
	])
);

export const getErrorCustomCode = pipe(
	path(['data', 'errors']),
	filter(negate(isValidationError)),
	cond([
		[isEmpty, constant(undefined)],
		[stubTrue, map('code')],
	])
);

/**
 * Gets form validation errors from `originalError`
 */
export const getValidationErrors = pipe(
	path(['data', 'errors']),
	filter(isValidationError),
	reduce((acc, { attribute, message }) => set(attribute, message, acc), {})
);

/**
 * Extracts all usually needed informations from graphQLError
 */
export const extractError = (originalError) => {
	const errorCode = getErrorCode(originalError);
	const errorMessages = getErrorMessages(originalError);
	const validationErrors = getValidationErrors(originalError);

	return { errorCode, errorMessages, originalError, validationErrors };
};

/**
 * Extracts soome metadata from gqlQueryAST.
 * @param {DocumentNode} gqlAST
 * @return {{queryName: string, operationId: string}}
 */
export function getDocumentMeta(gqlAST) {
	const {
		definitions: [
			// main definition
			{
				name: { value: definitionName },
				selectionSet: {
					selections: [
						// first selection of main definition is operationId
						{
							name: { value: operationId },
						},
					],
				},
			},
		],
	} = gqlAST;

	return { definitionName, operationId };
}

/**
 * Patches options optional callbacks, `options.onError` will be called with
 * originalError and `options.onCompleted` will be called with object unnested
 * from `operationId`
 * @param {string} operationId
 * @param {function|object} options
 * @return {ClientQueryOptions}
 */
export function normalizeReactApolloOptions(operationId, options) {
	if (isFunction(options)) {
		return pipe(options, (derivedOptions) =>
			normalizeReactApolloOptions(operationId, derivedOptions)
		);
	}

	const { onError, onCompleted } = options;

	return {
		...options,
		onError: onError && ((error) => onError(getOriginalError(error))),
		onCompleted: onCompleted && ((data) => onCompleted(omitDeep(isNull, data[operationId]))),
	};
}

export function omitTypename(input) {
	return omitDeep(['__typename'], input);
}

/**
 * Indicates loading state when using `cache-and-network` fetchPolicy
 * (it doesn't trigger loading on background data fetching)
 * @param query
 * @returns {boolean}
 */
export function isCacheAndNetworkLoading(queryResult) {
	return (
		(queryResult.loading && !queryResult.response) ||
		queryResult.networkStatus === NetworkStatus.refetch ||
		queryResult.networkStatus === NetworkStatus.setVariables
	);
}

export const mergeFnPaginatedData = (existing, incoming, { readField, variables }) => {
	if (!existing || !variables?.limit) {
		// Simply put, if you need to merge paginated data, you must pass in `limit` variable, otherwise, the data wont be
		// merged. This is necessary for simplify updating the query via Apollo's `cache.readQuery` and `cache.writeQuery`.
		// Calling `cache.writeQuery` invokes the merge function which created almost unsolvable problem when particular
		// collection item shall be removed - removing it via `writeQuery` did not worked as the merge function returned it
		// back. Apollo has `cache.modify` function, that circumvents merge function, but does not support collections and
		// its usage is thus _way_ more complicated for collection queries. Therefore we must pass the `limit` variable to
		// the queries we want merge and when you need overwrite cache data, simply omit the `limit` variable - this assumes
		// that `limit` variable is not a key argument (not listed in `keyArgs` in ApolloClient `typePolicies`).
		return incoming;
	}
	if (variables.after) {
		// The `incoming` is result of `fetchMore` call, honor the `incoming.pagingInfo` and append the `incoming.data`
		// _after_ the `existing.data`.
		return {
			...existing,
			...incoming,
			data: unionBy((item) => readField(`id`, item), existing.data, incoming.data),
		};
	}
	// As the limit is 1, the `incoming` is a result of the forced data update and should overwrite the previous result
	if (variables?.limit === 1 && incoming.data?.length === 0) {
		return incoming;
	}
	// The response is probably a result of re-fetching the data after some mutation. Do not change the `pagingInfo` as
	// it might be more accurate from previous `fetchMore` calls and prepend the `incoming.data` at the beginning.
	return {
		...incoming,
		...existing,
		data: unionBy((item) => readField(`id`, item), incoming.data, existing.data),
	};
};

const mergePaginatedData = (existing, incoming) => ({
	...existing,
	data: [...existing.data, ...incoming.data],
	pagingInfo: incoming.pagingInfo,
});

/**
 * Utility to simplify use of `fetchMore` callback. Several conventions must be hold:
 * 	1. underlying query contains dynamic variable `after` which sets pagination cursor,
 * 	2. field which is paginated must query `pagingInfo.nextPage`,
 * @param {object} queryResult of `useQuery` or `useLazyQuery` call.
 * @param {string} operationId of field which will be paginated.
 * @return {function} callback which will invoke `fetchMore` or performs no-op,
 * 	when `pagingInfo.nextPage` is empty
 */
export function createFetchMore(queryResult, operationId) {
	const response = queryResult.data?.[operationId];

	return !response?.pagingInfo?.nextPage
		? noop
		: () =>
				queryResult.fetchMore({
					variables: {
						after: last(response.data).id,
					},
					updateQuery: (previousResult, { fetchMoreResult }) =>
						!fetchMoreResult
							? previousResult
							: {
									[operationId]: mergePaginatedData(
										previousResult[operationId],
										fetchMoreResult[operationId]
									),
							  },
				});
}

/**
 * Utility to simplify use of `fetchMore` callback. Several conventions must be hold:
 * 	1. underlying query contains dynamic variable `after` which sets pagination cursor,
 * 	2. field which is paginated must query `pagingInfo.nextPage`,
 * 	3. Apollo cache must be configured to merge results,
 * @see https://www.apollographql.com/docs/react/caching/cache-field-behavior/#the-merge-function
 * @param {object} queryResult of `useQuery` or `useLazyQuery` call.
 * @param {string} operationId of field which will be paginated.
 * @param {(previousResult, nextResult) => void} onUpdateQuery callback function called to update query results.
 */
export function createFetchMoreApolloV3(queryResult, operationId, onUpdateQuery) {
	const response = queryResult.data?.[operationId];

	const hasFetchMore = !!response?.pagingInfo?.nextPage;

	if (!hasFetchMore) {
		return null;
	}

	return () =>
		queryResult.fetchMore({
			variables: {
				after: last(response.data).id,
			},
			...(onUpdateQuery && { updateQuery: onUpdateQuery }),
		});
}
